// Import Swiper styles

import 'swiper/css';

import HorizontalBarChart from '@components/HorizontalBarChart';
import RadialBarChart from '@components/RadialBarChart';
import ResultsKey from '@components/ResultsKey';
import ResultsPagination from '@components/ResultsPagination';
import ResultsPillarTextColumn from '@components/ResultsPillarTextColumn';
import { ResultsPillarType } from '@data/types';
import useQuestionnaireStore from '@store/questionnaire';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  getBarScores,
  getQuestion,
  getQuestionScores,
  getRegion,
} from 'utilities/scores';

interface Props extends ResultsPillarType {
  as?: keyof JSX.IntrinsicElements;
}

const ResultsPillar: React.FC<Props> = ({
  as: Component = 'div',
  htmlContent,
  tmHtmlContent,
  awsHtmlContent,
  awsButton,
  tmButton,
  backgroundColour,
  pillarIndex,
  charts = [],
}) => {
  const { pillarsStore } = useQuestionnaireStore();

  const { region } = useMemo(
    function () {
      const region = getRegion(pillarsStore);

      return {
        region,
      };
    },
    [pillarsStore]
  );

  return (
    <Component
      id={`pillar-${pillarIndex}`}
      data-pillarindex={pillarIndex}
      className={classNames('w-full flex', [
        backgroundColour === 'blue' ? 'bg-blue' : 'bg-[#F1F4F6]',
      ])}
      data-pdf-page
    >
      <div className="flex flex-grow flex-col w-full">
        <div className="result-pillar-container px-4 py-12 xl:px-0 md:py-16 xl:py-24 w-full max-w-outerGrid h-full flex gap-4 flex-col mx-auto relative">
          <ResultsPagination
            backgroundColour={backgroundColour}
            pillarIndex={pillarIndex}
          />
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="w-full lg:w-1/2">
              <h2 className="pillar-title font-lightNormal text-xs bg-blue-light text-blue rounded-tl-lg rounded-br-lg mb-4 inline-block uppercase px-4 py-1">
                Pillar 0{pillarIndex}
              </h2>
              <div
                className={classNames(
                  'prose prose-h3:font-lightNormal prose-h3:text-1xl',
                  {
                    'prose-h3:text-white': backgroundColour === 'blue',
                  }
                )}
                dangerouslySetInnerHTML={
                  htmlContent ? { __html: htmlContent } : undefined
                }
              ></div>
            </div>
            <div className="w-full lg:w-1/2 flex items-center">
              <ResultsKey
                className={classNames('grid grid-cols-2 w-full !-mb-4', {
                  '!text-white': backgroundColour === 'blue',
                  '!text-blue': backgroundColour !== 'blue',
                })}
                region={region}
              />
            </div>
          </div>

          {/* Mobile */}
          <div className="overflow-hidden !w-full md:hidden">
            <Swiper
              className={classNames('!w-full !max-w-full', {
                'swiper--blue': backgroundColour == 'blue',
              })}
              slidesPerView={1}
              spaceBetween={0}
              pagination={charts.length > 1}
              modules={[Pagination]}
            >
              {Boolean(charts.length) && (
                <div
                  className={classNames(
                    'flex flex-col md:flex-row gap-5 mt-4',
                    [backgroundColour === 'blue' ? 'text-white' : 'text-blue']
                  )}
                >
                  {charts.map(({ questionIndex, type }, index) => {
                    const { question, answers, maxScore } = getQuestion(
                      pillarIndex,
                      questionIndex
                    );
                    const scores =
                      type === 'radial'
                        ? getQuestionScores(
                            pillarsStore,
                            pillarIndex,
                            questionIndex,
                            region
                          )
                        : [];

                    const options =
                      type === 'bar'
                        ? getBarScores(
                            pillarsStore,
                            pillarIndex,
                            questionIndex,
                            answers,
                            region
                          )
                        : [];

                    return (
                      <SwiperSlide
                        key={[questionIndex, index].join()}
                        className="grow !h-auto"
                      >
                        <div
                          className={classNames(
                            'w-full h-full flex flex-col p-8 gap-5 border bg-white rounded-tl-[2rem] rounded-br-[2rem]',
                            [
                              backgroundColour === 'blue'
                                ? 'border-white border-opacity-20 bg-opacity-[0.06]'
                                : 'border-[#E1E1E1]',
                            ],
                            {
                              'lg:flex-row':
                                type === 'bar' && charts.length === 1,
                            }
                          )}
                        >
                          <div className="grow">
                            <div className="uppercase px-2 py-1 mb-4 inline-block text-xs bg-blue-light text-blue rounded-tl-lg rounded-br-lg">
                              Q{index + 1}
                            </div>
                            <h3
                              className={classNames('text-xs', [
                                backgroundColour == 'blue'
                                  ? 'text-white'
                                  : 'text-blue',
                              ])}
                            >
                              {question}
                            </h3>
                            <div className="mt-6">
                              {type === 'radial' && (
                                <div className="p-2">
                                  <RadialBarChart
                                    question={question}
                                    region={region}
                                    scores={[
                                      scores[0],
                                      scores[1],
                                      scores[2],
                                      scores[3],
                                    ]}
                                    max={maxScore || 20}
                                    index={index + 1}
                                    className={classNames('', [
                                      backgroundColour == 'blue'
                                        ? 'text-white'
                                        : 'text-blue',
                                    ])}
                                  />
                                </div>
                              )}
                              {type === 'bar' && (
                                <div className="w-full lg:w-3/4">
                                  <HorizontalBarChart
                                    data={options}
                                    region={region}
                                    max={maxScore || 20}
                                    index={index + 1}
                                    className={classNames('', [
                                      backgroundColour == 'blue'
                                        ? 'text-white'
                                        : 'text-blue',
                                    ])}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </div>
              )}
            </Swiper>
          </div>

          {/* Desktop */}
          <div className="hidden md:block">
            {Boolean(charts.length) && (
              <div
                className={classNames(
                  'result-pillar-charts flex flex-col md:flex-row gap-5 mt-4',
                  [backgroundColour === 'blue' ? 'text-white' : 'text-blue']
                )}
              >
                {charts.map(({ questionIndex, type }, index) => {
                  const { question, answers, maxScore } = getQuestion(
                    pillarIndex,
                    questionIndex
                  );
                  const scores =
                    type === 'radial'
                      ? getQuestionScores(
                          pillarsStore,
                          pillarIndex,
                          questionIndex,
                          region
                        )
                      : [];

                  const options =
                    type === 'bar'
                      ? getBarScores(
                          pillarsStore,
                          pillarIndex,
                          questionIndex,
                          answers,
                          region
                        )
                      : [];

                  return (
                    <div
                      className={classNames(
                        'w-full p-5 flex flex-col gap-5 border bg-white rounded-tl-[2rem] rounded-br-[2rem]',
                        [
                          backgroundColour === 'blue'
                            ? 'border-white border-opacity-20 bg-opacity-[0.06]'
                            : 'border-[#E1E1E1]',
                        ],
                        {
                          'lg:flex-row': type === 'bar' && charts.length === 1,
                        }
                      )}
                      key={[questionIndex, index].join()}
                    >
                      <div className="grow">
                        <div className="pillar-number uppercase px-2 py-1 mb-4 inline-block text-xs bg-blue-light text-blue rounded-tl-lg rounded-br-lg">
                          Q{index + 1}
                        </div>
                        <h3 className="text-xs">{question}</h3>
                      </div>
                      {type === 'radial' && (
                        <div className="p-2">
                          <RadialBarChart
                            question={question}
                            region={region}
                            scores={[
                              scores[0],
                              scores[1],
                              scores[2],
                              scores[3],
                            ]}
                            max={maxScore || 20}
                            index={index + 1}
                            className={classNames('', [
                              backgroundColour == 'blue'
                                ? 'text-white'
                                : 'text-blue',
                            ])}
                          />
                        </div>
                      )}
                      {type === 'bar' && (
                        <div className="w-full lg:w-3/4">
                          <HorizontalBarChart
                            data={options}
                            region={region}
                            max={maxScore || 20}
                            index={index + 1}
                            className={classNames('', [
                              backgroundColour == 'blue'
                                ? 'text-white'
                                : 'text-blue',
                            ])}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-4">
            <ResultsPillarTextColumn
              htmlContent={tmHtmlContent}
              backgroundColour={backgroundColour}
              button={tmButton}
            />
            <ResultsPillarTextColumn
              htmlContent={awsHtmlContent}
              backgroundColour={backgroundColour}
              button={awsButton}
            />
          </div>
        </div>
      </div>
    </Component>
  );
};

export default ResultsPillar;
